import React, {Dispatch, FC, SetStateAction, useMemo, useState} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  SettingsBannersEnum,
  useSettingsBanners,
} from '../../../../libs/shared/core/src/helpers/react/hooks/useSettingsBanners';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {AnalyticsDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/analyticsDataEventObject';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TextAreaBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dialog} from '../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogContent} from '../../../../libs/shared/ui/src/components/dialogs/dialogContent';
import {DialogFooter} from '../../../../libs/shared/ui/src/components/dialogs/dialogFooter';
import {DialogHeader} from '../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {LearnMoreButton} from '../../../components/common/callToAction/learnMoreButton';
import {useAnalyticsTrackingFrontendEvent} from '../../helpers/tracking/useAnalyticsTracking';
import {LIVE_DASHBOARD_HELP_CENTER_URL} from '../realTimeReport/constants';

/*
 * Props.
 */

interface BetaBannerProps {
  onClose: () => void;
  isDisplayedForTheFirstTime: boolean;
  panelId: AnalyticsPanelId;
}

/*
 * Styles.
 */

const StyledBetaBannerWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 26px 1fr 26px;
  grid-template-areas: '. banner close';
  padding: 10px;
  background-color: ${(p) => p.theme.palette.blue.shade20};
`;

const StyledCloseButtonWrapper = styled.div`
  grid-area: close;
`;

const StyledContentWrapperDiv = styled.div`
  grid-area: banner;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
`;

const StyledTextDiv = styled.div`
  margin-right: 4px;
`;

const StyledTellUsMoreDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.semibold};
`;

const StyledTextAreaBox = styled(TextAreaBox)`
  min-height: 144px;
`;

/*
 * Component.
 */

export const BetaBanner: FC<BetaBannerProps> = ({onClose, isDisplayedForTheFirstTime, panelId}) => {
  const theme = useUserTheme();
  const getPanelId = useMemo(() => () => panelId, [panelId]);
  const trackAnalyticsFrontendEvent = useAnalyticsTrackingFrontendEvent(getPanelId);
  const [displayFeedbackDialog, setShowDisplayFeedbackDialog] = useState(false);
  const [feedbackContent, setFeedbackContent] = useState('');
  const localize = useLocalize();
  const {showBanner} = useSettingsBanners();

  const trackVote = (isUpVote?: boolean) => {
    trackAnalyticsFrontendEvent(
      DataEventActionsEnum.CLICK,
      AnalyticsDataEventObject.BETA_FEEDBACK,
      isUpVote === undefined ? undefined : {is_up_vote: isUpVote},
    );

    setShowDisplayFeedbackDialog(true);
  };

  const submitFeedback = () => {
    trackAnalyticsFrontendEvent(DataEventActionsEnum.CLICK, AnalyticsDataEventObject.BETA_FEEDBACK_COMMENT, {
      comment: feedbackContent,
    });
    setFeedbackContent('');
    showBanner({
      type: SettingsBannersEnum.SUCCESS,
      message: <Trans id="beta-banner-success">Feedback sent, thank you!</Trans>,
    });
    setShowDisplayFeedbackDialog(false);
    onClose();
  };

  const feedbackDialogContent = (() => {
    if (isDisplayedForTheFirstTime) {
      return (
        <>
          <StyledTextDiv>
            <Trans id="beta-banner-message-first-time">
              We&apos;re adding new functionality! What do you think of this live dashboard?
            </Trans>
          </StyledTextDiv>
          <Button size={VisualSizesEnum.SMALL} onClick={() => trackVote(true)}>
            <Icon name="thumbsup" color={theme.greys.shade80} size={16} />
          </Button>
          <Button size={VisualSizesEnum.SMALL} onClick={() => trackVote(false)}>
            <Icon name="thumbsdownSmall" color={theme.greys.shade80} size={16} />
          </Button>
        </>
      );
    }

    return (
      <>
        <StyledTextDiv>
          <Trans id="beta-banner-message-follow-up">
            Help us improve analytics. What&apos;s missing from the live dashboard?
          </Trans>
        </StyledTextDiv>
        <Button
          size={VisualSizesEnum.SMALL}
          onClick={() => trackVote()}
          leftIcon="composeSmall"
          leftIconColor={theme.greys.shade80}
        >
          <Trans id="beta-banner-button-label-follow-up">Send us feedback</Trans>
        </Button>
      </>
    );
  })();

  return (
    <StyledBetaBannerWrapperDiv>
      <StyledContentWrapperDiv>
        {feedbackDialogContent}
        <LearnMoreButton
          helpPageUrl={LIVE_DASHBOARD_HELP_CENTER_URL}
          variant={ButtonVariantsEnum.LINK}
          size={VisualSizesEnum.SMALL}
        />
      </StyledContentWrapperDiv>
      <StyledCloseButtonWrapper>
        <IconButton
          label={<Trans id="beta-banner-close">Close beta banner</Trans>}
          iconName="closeSmall"
          onClick={onClose}
        />
      </StyledCloseButtonWrapper>
      {maybeDisplayFeedbackDialog(
        displayFeedbackDialog,
        isDisplayedForTheFirstTime,
        setShowDisplayFeedbackDialog,
        feedbackContent,
        setFeedbackContent,
        submitFeedback,
        localize,
      )}
    </StyledBetaBannerWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeDisplayFeedbackDialog(
  displayFeedbackDialog: boolean,
  isDisplayedForTheFirstTime: boolean,
  setDisplayFeedbackDialog: (val: boolean) => void,
  feedbackContent: string,
  setFeedbackContent: Dispatch<SetStateAction<string>>,
  submitFeedback: () => void,
  localize: Localizer,
) {
  if (!displayFeedbackDialog) {
    return null;
  }

  const skipFeedback = () => {
    setFeedbackContent('');
    setDisplayFeedbackDialog(false);
  };

  const dialogTitle = isDisplayedForTheFirstTime ? (
    <Trans id="beta-banner-dialog-header-first-time">Thanks for your feedback!</Trans>
  ) : (
    <Trans id="beta-banner-dialog-header-follow-up">Send us feedback</Trans>
  );

  const title = isDisplayedForTheFirstTime ? (
    <Trans id="beta-banner-dialog-title-first-time">Tell us more:</Trans>
  ) : (
    <Trans id="beta-banner-dialog-title-follow-up">What&apos;s missing from the live dashboard?</Trans>
  );

  const placeholder = isDisplayedForTheFirstTime
    ? defineMessage({
        id: 'beta-banner-dialog-text-area-placeholder-first-time',
        message: 'What do you think of this new live dashboard?',
      })
    : defineMessage({
        id: 'beta-banner-dialog-text-area-placeholder-follow-up',
        message: 'Share your feedback',
      });

  return (
    <Dialog onRequestClose={() => setDisplayFeedbackDialog(false)} width={600}>
      <DialogHeader title={dialogTitle} />
      <DialogContent>
        <StyledTellUsMoreDiv>{title}</StyledTellUsMoreDiv>
        <StyledTextAreaBox
          value={feedbackContent}
          onChange={setFeedbackContent}
          placeholder={localize(placeholder)}
          shouldAllowResize
        />
      </DialogContent>
      <DialogFooter>
        <ButtonGroupSpacer />
        <ButtonGroup>
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={skipFeedback}>
            <Trans id="beta-banner-dialog-skip-button">Skip</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} isDisabled={!feedbackContent} onClick={submitFeedback}>
            <Trans id="beta-banner-dialog-submit-button">Submit</Trans>
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
}
